export const endpoints = {
  // Organizations endpoints
  organizations: {
    all: 'organizations/all',
    byId: (id: string) => `organizations/${id}`,
    filtered: '/organizations/filtered',
    create: '/organizations',
    update: (id: string) => `/organizations/${id}`,
    delete: (id: string) => `/organizations/${id}`,
  },

  // Contacts endpoints
  contacts: {
    all: '/contacts/all',
    byId: (id: string) => `/contacts/${id}`,
    filtered: '/contacts/filtered',
    create: '/contacts',
    update: (id: string) => `/contacts/${id}`,
    delete: (id: string) => `/contacts/${id}`,
    activities: (id: string) => `/contacts/${id}/activities`,
  },

  // Segments endpoints
  segments: {
    all: '/segments/all',
    byId: (id: string) => `/segments/${id}`,
    filtered: '/segments/filtered',
    create: '/segments',
    update: (id: string) => `/segments/${id}`,
    delete: (id: string) => `/segments/${id}`,
  },

  // Life Cycles endpoints
  lifeCycles: {
    all: '/life-cycles/all',
    byId: (id: string) => `/life-cycles/${id}`,
    filtered: '/life-cycles/filtered',
    create: '/life-cycles',
    update: (id: string) => `/life-cycles/${id}`,
    delete: (id: string) => `/life-cycles/${id}`,
  },

  // Contact Types endpoints
  contactTypes: {
    all: '/contact-types/all',
    byId: (id: string) => `/contact-types/${id}`,
    filtered: '/contact-types/filtered',
    create: '/contact-types',
    update: (id: string) => `/contact-types/${id}`,
    delete: (id: string) => `/contact-types/${id}`,
  },

  // Contact Statuses endpoints
  contactStatuses: {
    all: '/contact-statuses/all',
    byId: (id: string) => `/contact-statuses/${id}`,
    filtered: '/contact-statuses/filtered',
    create: '/contact-statuses',
    update: (id: string) => `/contact-statuses/${id}`,
    delete: (id: string) => `/contact-statuses/${id}`,
    reorder: '/contact-statuses/reorder',
  },

  // Marketing Lists endpoints
  marketingLists: {
    all: '/marketing/lists/all',
    list: '/marketing/lists',
    byId: (id: string) => `/marketing/lists/${id}`,
    create: '/marketing/lists',
    update: (id: string) => `/marketing/lists/${id}`,
    delete: (id: string) => `/marketing/lists/${id}`,
    contacts: (id: string) => `/marketing/lists/${id}/contacts`,
    refreshDynamic: '/marketing/lists/refresh-dynamic',
    refresh: (id: string) => `/marketing/lists/${id}/refresh`,
    preview: '/marketing/lists/preview',
    aiGenerate: '/marketing/lists/ai-generate-filters',
    filterableProperties: '/marketing/lists/metadata/properties',
  },

  // Marketing Email Campaigns endpoints
  marketingCampaigns: {
    all: '/marketing/campaigns',
    create: '/marketing/campaigns',
    byId: (id: string) => `/marketing/campaigns/${id}`,
    update: (id: string) => `/marketing/campaigns/${id}`,
    send: '/marketing/campaigns/send',
    stats: (id: string) => `/marketing/campaigns/${id}/stats`,
    delete: (id: string) => `/marketing/campaigns/${id}`,
  },

  // Marketing Email Templates endpoints
  marketingTemplates: {
    all: '/marketing/templates',
    byId: (id: string) => `/marketing/templates/${id}`,
    create: '/marketing/templates',
    update: (id: string) => `/marketing/templates/${id}`,
    delete: (id: string) => `/marketing/templates/${id}`,
    preview: (id: string) => `/marketing/templates/${id}/preview`,
    duplicate: (id: string) => `/marketing/templates/${id}/duplicate`,
    statistics: '/marketing/templates/statistics',
  },

  // Marketing Events endpoints
  marketingEvents: {
    filtered: '/management/marketing/events',
    byId: (id: string) => `/management/marketing/events/${id}`,
    create: '/management/marketing/events',
    update: (id: string) => `/management/marketing/events/${id}`,
    delete: (id: string) => `/management/marketing/events/${id}`,
    metrics: (id: string) => `/management/marketing/events/${id}/metrics`,
    checkIn: (id: string) => `/management/marketing/events/${id}/check-in`,
    registerGuest: (id: string) => `/management/marketing/events/${id}/register-guest`,
    updateAttendeeStatus: (id: string) => `/management/marketing/events/${id}/attendees/status`,
    invite: (id: string) => `/management/marketing/events/${id}/invite`,
  },

  // Marketing Sequences endpoints
  sequences: {
    filtered: '/management/marketing/sequences/filtered',
    byId: (id: string) => `/management/marketing/sequences/${id}`,
    create: '/management/marketing/sequences',
    update: (id: string) => `/management/marketing/sequences/${id}`,
    delete: (id: string) => `/management/marketing/sequences/${id}`,
    activate: (id: string) => `/management/marketing/sequences/${id}/activate`,
    pause: (id: string) => `/management/marketing/sequences/${id}/pause`,
    archive: (id: string) => `/management/marketing/sequences/${id}/archive`,
    duplicate: (id: string) => `/management/marketing/sequences/${id}/duplicate`,
    enroll: (id: string) => `/management/marketing/sequences/${id}/enroll`,
    unenroll: (id: string, enrollmentId: string) =>
      `/management/marketing/sequences/${id}/unenroll/${enrollmentId}`,
    enrollments: (id: string) => `/management/marketing/sequences/${id}/enrollments`,
    stats: (id: string) => `/management/marketing/sequences/${id}/stats`,
    events: (id: string) => `/management/marketing/sequences/${id}/events`,
    test: (id: string) => `/management/marketing/sequences/${id}/test`,
  },

  // Marketing Forms endpoints
  marketingForms: {
    all: '/marketing/forms',
    byId: (id: string) => `/marketing/forms/${id}`,
    create: '/marketing/forms',
    update: (id: string) => `/marketing/forms/${id}`,
    delete: (id: string) => `/marketing/forms/${id}`,
    embedScript: (id: string, type: 'iframe' | 'vanilla' | 'react') =>
      `/marketing/forms/${id}/embed-script?type=${type}`,
    submissions: (id: string) => `/marketing/forms/${id}/submissions`,
    analytics: (id: string) => `/marketing/forms/${id}/analytics`,
    fieldCompletion: (id: string) => `/marketing/forms/${id}/analytics/field-completion`,
    conversionFunnel: (id: string) => `/marketing/forms/${id}/analytics/conversion-funnel`,
    publicFormBySlug: (slug: string) => `/public/marketing/forms/form/${slug}`,
    publicSubmit: (slug: string) => `/public/marketing/forms/submit/${slug}`,
  },

  // Countries endpoints
  countries: {
    all: '/countries/all',
    byId: (id: string) => `/countries/${id}`,
    filtered: '/countries/filtered',
    create: '/countries',
    update: (id: string) => `/countries/${id}`,
    delete: (id: string) => `/countries/${id}`,
  },

  // Legal Entities endpoints
  legalEntities: {
    all: 'legal-entities',
    byId: (id: string) => `legal-entities/${id}`,
  },

  // Orders endpoints
  orders: {
    byId: (id: string) => `/orders/${id}`,
    filtered: '/orders/filtered',
  },

  // Cancellation endpoints
  cancellation: {
    createOrder: () => `/cancellations`,
    getOrderById: (id: string) => `/cancellations/${id}`,
    cancellableItems: (orderId: string) => `/cancellation/orders/${orderId}/cancellable-items`,
    updateOrder: (id: string) => `/cancellations/${id}`,
    deleteOrder: (id: string) => `/cancellations/${id}`,
    confirmOrder: (id: string) => `/cancellations/${id}/confirm`,
    replaceMilestones: () => `/cancellations/replace-milestones`,
    getOrders: (organizationId: string) =>
      `/cancellations/organization/${organizationId}/cancellations`,

    getCancellationOrdersByPurchaseOrder: (orderId: string) => `/cancellations/orders/${orderId}`,
  },

  // Organization Documents endpoints
  organizationDocuments: {
    byId: (id: string) => `/organization-documents/${id}`,
    filtered: '/organization-documents/filtered',
  },

  // LLM Config endpoints
  llmConfig: {
    all: '/settings/llm-config',
    byOrganization: (orgId: string) => `/settings/llm-config/organization/${orgId}`,
    byId: (id: string) => `/settings/llm-config/${id}`,
    create: '/settings/llm-config',
    update: (id: string) => `/settings/llm-config/${id}`,
    remove: (id: string) => `/settings/llm-config/${id}`,
    test: (id: string) => `/settings/llm-config/${id}/test`,
    models: '/settings/llm-config/models',
    modelsByConfig: (id: string) => `/settings/llm-config/${id}/models`,
  },

  // Reference Data endpoints
  referenceData: {
    countries: '/countries',
    lifeCycles: '/life-cycles',
    segments: '/segments',
    industries: '/industries',
    staffUsers: '/users/staff',
    documentTypes: '/document-types',
    termConditions: '/term-conditions',
    partners: '/partners',
    businessUnits: '/business-units',
  },

  // Requests endpoints
  requests: {
    byId: (id: string) => `/requests/${id}`,
    filtered: '/requests/filtered',
    approve: (id: string) => `/requests/${id}/approve`,
    update: (id: string) => `/requests/${id}`,
  },

  // Request Users endpoints
  requestUsers: {
    filtered: '/request-users/filtered',
  },

  // Subscriptions endpoints
  subscriptions: {
    byId: (id: string) => `/subscriptions/${id}`,
    filtered: '/subscriptions/filtered',
  },

  // Billing endpoints
  billing: {
    subscription: (subscriptionId: string) => `/subscriptions/${subscriptionId}/billing`,
    organization: (organizationId: string) => `/organizations/${organizationId}/billing`,
    milestonesToBeBilledByOrg: () => `/billing/milestones-to-be-billed`,
    createBillingOrder: '/billing',
    getBillingOrderById: (orderId: string) => `/billing/${orderId}`,
    approveBillingOrder: (orderId: string) => `/billing/requests/${orderId}/approve`,
    rejectBillingOrder: (orderId: string) => `/billing/requests/${orderId}/reject`,
  },

  // Compass Inventory endpoints
  compassInventory: {
    filtered: 'compass/inventory/filtered',
    regions: 'compass/inventory/region/all',
    serviceNames: 'compass/inventory/serviceName/all',
    serviceCategories: 'compass/inventory/service-category/all',
    providerNames: 'compass/inventory/providerName/all',
    tagKeys: 'compass/inventory/tag-keys/all',
    tagValues: (key: string) => `compass/inventory/tag-values/${key}/all`,
  },

  // Plans endpoints
  plans: {
    all: '/plans',
    byId: (id: string) => `/plans/${id}`,
    bySku: (skuId: string) => `/plans?sku=${skuId}`,
  },

  // Opportunities endpoints
  opportunities: {
    all: '/opportunities',
    byId: (id: string) => `/opportunities/${id}`,
    byOrganization: (organizationId: string) => `/opportunities/organization/${organizationId}`,
    filtered: '/opportunities/filtered',
    create: '/opportunities',
    update: (id: string) => `/opportunities/${id}`,
    updateStage: (id: string) => `/opportunities/${id}/stage`,
    delete: (id: string) => `/opportunities/${id}`,
    convertToOrder: (id: string) => `/opportunities/${id}/convert-to-order`,
    updateQuotation: (opportunityId: string) => `/opportunities/${opportunityId}/quotation`,
    addMilestone: (opportunityId: string) => `/opportunities/${opportunityId}/milestones`,
    updateMilestone: (opportunityId: string, milestoneId: string) =>
      `/opportunities/${opportunityId}/milestones/${milestoneId}`,
    deleteMilestone: (opportunityId: string, milestoneId: string) =>
      `/opportunities/${opportunityId}/milestones/${milestoneId}`,
    history: (opportunityId: string) => `/opportunities/${opportunityId}/history`,
    markAsLost: (id: string) => `/opportunities/${id}/mark-as-lost`,
    reactivate: (id: string) => `/opportunities/${id}/reactivate`,
    summary: '/opportunities/summary',
    items: '/opportunity-items',
  },

  // Opportunity Stages endpoints
  opportunityStages: {
    root: '/opportunity-stages',
    detail: (id: string) => `/opportunity-stages/${id}`,
    all: '/opportunity-stages',
    paginated: '/opportunity-stages/paginated',
    reorder: '/opportunity-stages/reorder',
  },

  // User Integrations endpoints
  userIntegrations: {
    base: 'user-integrations',
    status: (provider: string) => `/user-integrations/${provider}`,
    update: (provider: string) => `/user-integrations/${provider}/settings`,
    sync: (provider: string) => `/user-integrations/${provider}/sync`,
    disconnect: (provider: string) => `/user-integrations/${provider}`,
    cleanup: (provider: string) => `/user-integrations/${provider}/cleanup`,
    connect: 'user-integrations/google/connect', // New dedicated integration endpoint
    forceReset: (provider: string) => `/user-integrations/${provider}/force-reset`,
    // Catalog Section Types endpoints
  },
  catalogSections: {
    all: '/catalog/section-types',
    byId: (id: string) => `/catalog/section-types/${id}`,
    byKey: (key: string) => `/catalog/section-types/key/${key}`,
    create: '/catalog/section-types',
    update: (id: string) => `/catalog/section-types/${id}`,
    delete: (id: string) => `/catalog/section-types/${id}`,
  },

  // Partner Catalog endpoints
  // Odoo Sync endpoints
  odooSync: {
    syncProducts: '/odoo-sync/product-sync/sync-products',
    comparisonReport: '/odoo-sync/product-sync/comparison-report',
    syncPricelist: '/odoo-sync/product-sync/sync-pricelist',
    syncOdooIds: '/odoo-sync/product-sync/sync-odoo-ids',
    syncAll: '/odoo-sync/product-sync/sync-all',
    status: '/odoo-sync/product-sync/status',
    cancel: '/odoo-sync/product-sync/cancel',
  },

  partnerCatalogs: {
    all: '/catalog/partner-catalogs',
    byId: (id: string) => `/catalog/partner-catalogs/${id}`,
    create: '/catalog/partner-catalogs',
    delete: (id: string) => `/catalog/partner-catalogs/${id}`,
  },

  // Presales Tickets endpoints
  presalesTickets: {
    base: () => '/presales-tickets',
    byId: (id: string) => `/presales-tickets/${id}`,
    move: (id: string) => `/presales-tickets/${id}/move`,
    documents: (ticketId: string) => `/presales-tickets/${ticketId}/documents`,
    document: (ticketId: string, docId: string) =>
      `/presales-tickets/${ticketId}/documents/${docId}`,
  },

  // Presales Ticket Stages endpoints
  presalesTicketStages: {
    base: () => '/presales-ticket-stages',
    byId: (id: string) => `/presales-ticket-stages/${id}`,
  },

  // Presales Request Types endpoints
  presalesRequestTypes: '/presales-request-types',
  presalesRequestType: (id: string) => `/presales-request-types/${id}`,

  // Presales Service Types endpoints
  presalesServiceTypes: '/presales-service-types',
  presalesServiceType: (id: string) => `/presales-service-types/${id}`,

  // Partner Sections endpoints
  partnerSections: {
    all: '/catalog/partner-sections',
    byCatalog: (catalogId: string) => `/catalog/partner-sections/catalog/${catalogId}`,
    byId: (id: string) => `/catalog/partner-sections/${id}`,
    create: '/catalog/partner-sections',
    update: (id: string) => `/catalog/partner-sections/${id}`,
    delete: (id: string) => `/catalog/partner-sections/${id}`,
  },

  // Product Sections endpoints
  productSections: {
    all: '/catalog/product-sections',
    byCatalog: (catalogId: string) => `/catalog/product-sections/catalog/${catalogId}`,
    byId: (id: string) => `/catalog/product-sections/${id}`,
    create: '/catalog/product-sections',
    update: (id: string) => `/catalog/product-sections/${id}`,
    delete: (id: string) => `/catalog/product-sections/${id}`,
  },

  // Odoo Manual Link endpoints
  odooLink: {
    searchOdoo: (entity: string) => `/odoo-sync/odoo-link/${entity}/search-odoo`,
    unlinked: (entity: string) => `/odoo-sync/odoo-link/${entity}/unlinked`,
    link: (entity: string, id: string) => `/odoo-sync/odoo-link/${entity}/${id}/link`,
    unlink: (entity: string, id: string) => `/odoo-sync/odoo-link/${entity}/${id}/unlink`,
    pushToOdoo: (entity: string, id: string) => `/odoo-sync/odoo-link/${entity}/${id}/push-to-odoo`,
  },

  // Feature Flags endpoints
  featureFlags: {
    all: '/feature-flags',
    active: '/feature-flags/active',
    byId: (id: string) => `/feature-flags/${id}`,
    toggle: (id: string) => `/feature-flags/${id}/toggle`,
    check: (key: string) => `/feature-flags/check/${key}`,
  },
};
